import { useEffect, useRef } from 'react';
import { useAuthStore } from '../Auth.store';
import { WebViewAuthService } from '../WebView.auth';
import Http from 'src/api/Http/Http';

/**
 * Hook to handle WebView authentication
 *
 * This hook:
 * 1. Detects if we're running in a WebView
 * 2. Sets up a listener for auth messages from React Native
 * 3. Requests authentication if not already authenticated
 * 4. Handles received token
 */
export const useWebViewAuth = () => {
  const { isWebView, isLoggedIn, handleWebViewToken } = useAuthStore();
  const webViewAuthRequested = useRef<boolean>(false);

  // Set up listener for auth messages from React Native
  useEffect(() => {
    // Only proceed if we're in a WebView
    if (!isWebView) {
      return;
    }

    // Handle token when received
    const cleanup = WebViewAuthService.listenForAuthMessages((token) => {
      // Set the token in auth store
      handleWebViewToken(token);

      // Update HTTP client with the token
      const http = new Http();
      http.setBearer(token);
    });

    return cleanup;
  }, [isWebView, handleWebViewToken]);

  // Request auth from native app if needed
  useEffect(() => {
    if (isWebView && !isLoggedIn && !webViewAuthRequested.current) {
      // Mark as requested to prevent duplicate requests
      webViewAuthRequested.current = true;
      // Request auth token from React Native
      WebViewAuthService.requestAuthFromNative();
    }
  }, [isWebView, isLoggedIn]);

  return { isWebView };
};
